<template>
  <div>
    <div class="medication-target-selects">
      <el-select
        v-close-on-zoom
        :value="state.check.target"
        :disabled="readonly"
        class="target-select"
        @input="value => updateTarget(value)"
      >
        <el-option value="ANY" :label="'Was ever'" />
        <el-option value="CURRENT" label="Is current administration" />
        <el-option value="TRIGGERING" label="Was triggering administration" />
      </el-select>
      <help-tooltip v-if="matchingActionError" type="error">
        <span>{{ matchingActionError.message }}</span>
      </help-tooltip>
      <medication-fluid-selector
        :selected="state.check.id"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :dosage-form-filter="
          dosageForm =>
            dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')
        "
        :is-global="isGlobal"
        @select="
          (treatmentType, treatmentId) => {
            if (treatmentType === 'MEDICATION') {
              selectMedication(treatmentId);
            } else if (treatmentType === 'FLUID') {
              selectFluid(treatmentId);
            }
          }
        "
      />
      <help-tooltip v-if="medFluidError" type="error">
        <span>{{ medFluidError.message }}</span>
      </help-tooltip>
      <help-tooltip v-if="ivDisabledError" type="error">
        <span>{{ ivDisabledError.message }}</span>
      </help-tooltip>
      <md-field class="bag-volume-select-field">
        <infusion-bag-volume-select
          :value="state.check.bagVolumes"
          :disabled="readonly"
          :default-any="true"
          :available="isGlobal ? null : availableBagVolumes"
          @input="bagVolumeKeys => updateBagVolumes(bagVolumeKeys)"
        />
      </md-field>
      <help-tooltip v-if="bagVolumeError" type="error">
        <span>{{ bagVolumeError.message }}</span>
      </help-tooltip>
      <div class="method-input-group el-input el-input-group el-input-group--prepend">
        <div class="el-input-group__prepend">with dosage</div>
        <div :style="{ display: 'flex' }">
          <el-select
            v-close-on-zoom
            :value="state.check.method"
            :disabled="readonly"
            class="method-select"
            @input="value => updateMethod(value)"
          >
            <el-option value="DROP_SET" :label="'Drip set'" />
            <el-option value="IV_PUMP" :label="'IV pump'" />
          </el-select>
          <help-tooltip v-if="methodDisabledError" type="error">
            <span>{{ methodDisabledError.message }}</span>
          </help-tooltip>
        </div>
      </div>
    </div>

    <div v-if="state.check.method==='DROP_SET'" class="dripset-container">
      <div class="dripset-label">Drip sets</div>
      <md-field class="dripset-select">
        <md-select
          :value="state.check.dripSets"
          :multiple="true"
          placeholder="Any drip sets"
          :disabled="readonly || !availableTubings.length"
          @md-selected="tubingKeys => updateDripSet(tubingKeys)"
        >
          <md-option
            v-for="tubing in availableTubings"
            :key="tubing.key"
            :value="tubing.key"
          >
            {{ tubing.label.en }}
          </md-option>
        </md-select>
      </md-field>
      <help-tooltip v-if="dripSetError" type="error">
        <span>{{ dripSetError.message }}</span>
      </help-tooltip>
      <dosage-count-check
        v-if="state.check.method === 'DROP_SET'"
        :disabled="readonly"
        mode="DOSAGE"
        prefix="drops between"
        :dosage-available="true"
        :count-available="false"
        :dosage="state.check.dripSetDosage"
        :units="state.check.dripSetUnits"
        :units-available="[
          { label: 'Drops per minute', value: 'DROPS_PER_MINUTE' },
          { label: 'mL/hr', value: 'ML_HR'},
        ]"
        class="dosage-count-check"
        @updateDosage="updateDripSetDosage"
        @updateUnits="updateDripSetUnits"
      />
    </div>

    <div class="dosage-container">
      <el-card v-if="state.check.method === 'IV_PUMP'" class="pump-overview">
        <el-select
          v-close-on-zoom
          :value="state.check.function"
          :disabled="readonly"
          class="function-select"
          @input="value => updateFunction(value)"
        >
          <el-option value="SET_RATE" :label="'Set Rate'" />
          <el-option value="FORMULARY" :label="'Formulary'" />
          <el-option value="CUSTOM_DOSE" :label="'Custom Dose'" />
        </el-select>
        <div style="display: flex">
          <el-checkbox
            label="Is Primary ?"
            :value="state.check.isPrimaryPump"
            :disabled="readonly"
            @input="value => updateIsPrimaryPump(value)"
          />
          <help-tooltip v-if="secondaryDisabledError" type="error">
            <span>{{ secondaryDisabledError.message }}</span>
          </help-tooltip>
        </div>
      </el-card>

      <div v-if="state.check.method === 'IV_PUMP'">
        <el-card v-if="state.check.function === 'FORMULARY'">
          <el-select
            v-close-on-zoom
            :value="state.check.formulary ? JSON.stringify(state.check.formulary) : null"
            :disabled="readonly"
            class="formulary-select"
            placeholder="Formulary"
            @input="
              value => {
                updateFormulary(JSON.parse(value));
              }
            "
          >
            <el-option
              v-for="(formulary, index) in availableFormularies"
              :key="index"
              :value="JSON.stringify(formulary)"
              :label="formularyLabel(formulary)"
            />
          </el-select>
          <help-tooltip v-if="formularyDisabledError" type="error">
            <span>{{ formularyDisabledError.message }}</span>
          </help-tooltip>
        </el-card>
        <el-card v-if="state.check.function === 'CUSTOM_DOSE'">
          <el-select
            v-close-on-zoom
            :value="state.check.customDosingType"
            :disabled="readonly"
            class="formulary-select"
            placeholder="Dosing Type"
            @input="value => updateDosingType(value)"
          >
            <el-option
              v-for="(dosingType) in availableDosingTypes"
              :key="dosingType.value"
              :value="dosingType.value"
              :label="dosingType.label"
            />
          </el-select>
          <help-tooltip v-if="dosingTypeDisabledError" type="error">
            <span>{{ dosingTypeDisabledError.message }}</span>
          </help-tooltip>
        </el-card>
        <dosage-count-check
          v-if="state.check.function === 'CUSTOM_DOSE'"
          :disabled="readonly"
          mode="DOSAGE"
          prefix="Dose between"
          :suffix="customDosageUnitLabel"
          :dosage-available="true"
          :count-available="false"
          :dosage="state.check.customDosage"
          class="dosage-count-check"
          @updateDosage="updateCustomDosage"
        />
        <dosage-count-check
          :disabled="readonly"
          mode="DOSAGE"
          prefix="VTBI between"
          suffix="mL"
          :dosage-available="true"
          :count-available="false"
          :dosage="state.check.vtbiDosage"
          class="dosage-count-check"
          @updateDosage="updateVtbiDosage"
        />
        <dosage-count-check
          v-if="state.check.function === 'SET_RATE'"
          :disabled="readonly"
          mode="DOSAGE"
          prefix="Rate between"
          suffix="mL/hr"
          :dosage-available="true"
          :count-available="false"
          :dosage="state.check.rateDosage"
          class="dosage-count-check"
          @updateDosage="updateRateDosage"
        />
        <dosage-count-check
          v-if="
            state.check.function === 'SET_RATE' ||
              state.check.function === 'CUSTOM_DOSE'
          "
          :disabled="readonly"
          mode="DOSAGE"
          prefix="Timeframe between"
          suffix="minutes"
          :dosage-available="true"
          :count-available="false"
          :dosage="state.check.timeframe"
          class="dosage-count-check"
          @updateDosage="updateTimeframe"
        />
        <dosage-count-check
          v-if="patientWeightAvailable"
          :disabled="readonly"
          mode="DOSAGE"
          prefix="Patient weight between"
          suffix="kg"
          :dosage-available="true"
          :count-available="false"
          :dosage="state.check.patientWeightDosage"
          class="dosage-count-check"
          @updateDosage="updatePatientWeightDosage"
        />
      </div>
    </div>
    <el-card v-if="state.check.id" class="infusion-location-options-container">
      <el-select
        v-close-on-zoom
        :value="state.check.ivInfusionLocation"
        :disabled="readonly"
        placeholder="Any infusion location"
        class="iv-infusion-location-select"
        @input="value => updateIvInfusionLocation(value)"
      >
        <el-option value="IV_SITE" label="IV Site" />
        <el-option value="MEDICATION" label="Medication" />
        <el-option value="FLUID" label="Fluid" />
      </el-select>
      <iv-location-select
        v-if="state.check.ivInfusionLocation === 'IV_SITE'"
        :iv-location="state.check.ivInfusionLocationIv || {}"
        :case-conf="emitter.caseConf"
        :is-global="isGlobal"
        :readonly="readonly"
        :iv-category-error="ivCategoryError"
        :iv-port-error="ivPortError"
        :iv-site-error="ivSiteError"
        :iv-side-error="ivSideError"
        @update="updateIvInfusionLocationIv"
      />

      <medication-fluid-selector
        v-if="state.check.ivInfusionLocation === 'MEDICATION'"
        :multiple="true"
        :show-fluids="false"
        :selected="state.check.ivInfusionLocationMedications || []"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :dosage-form-filter="
          dosageForm =>
            dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')
        "
        :is-global="isGlobal"
        @select="
          (treatmentType, treatmentIds) => {
            updateIvInfusionLocationMedications(treatmentIds);
          }
        "
      />
      <help-tooltip v-if="locationMedError" type="error">
        <span>{{ locationMedError.message }}</span>
      </help-tooltip>

      <medication-fluid-selector
        v-if="state.check.ivInfusionLocation === 'FLUID'"
        :multiple="true"
        :show-meds="false"
        :selected="state.check.ivInfusionLocationFluids || []"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :is-global="isGlobal"
        @select="
          (treatmentType, treatmentIds) => {
            updateIvInfusionLocationFluids(treatmentIds);
          }
        "
      />
      <help-tooltip v-if="locationFluidError" type="error">
        <span>{{ locationFluidError.message }}</span>
      </help-tooltip>
    </el-card>
  </div>
</template>

<script>
import merge from 'lodash/merge';
import omit from 'lodash/omit';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import MedicationFluidSelector from './common/MedicationFluidSelector';
import InfusionBagVolumeSelect from '~lib/common/components/medications/InfusionBagVolumeSelect.vue';
import DosageCountCheck from './common/DosageCountCheck.vue';
import IvLocationSelect from './common/IVLocationSelect.vue';
import infusionCheckMixins from './common/InfusionCheckMixins';

import iv from '~lib/common/config/iv';
import {
  infusionBagVolumes,
  infusionDosingTypes,
  unitTypes,
} from '~lib/common/config/medication';
import { fluids } from '~lib/common/config/fluids'

export default {
  name: 'infusion-check',
  components: {
    HelpTooltip,
    MedicationFluidSelector,
    InfusionBagVolumeSelect,
    DosageCountCheck,
    IvLocationSelect,
  },
  mixins: [ infusionCheckMixins ],
  props: [
    'medications',
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
  ],
  data() {
    const check = this.getData(this.ikey);
    const defaultCheck = {
          id: null,
          target: 'ANY',
          bagVolumes: [],
          method: this.isOnlyPump() ? 'IV_PUMP' : 'DROP_SET',
          function: 'SET_RATE',
          isPrimaryPump: true,
          dripSetDosage: {
            min: null,
            max: null,
          },
          dripSetUnits: 'DROPS_PER_MINUTE',
          vtbiDosage: {
            min: null,
            max: null,
          },
          rateDosage: {
            min: null,
            max: null,
          },
          patientWeightDosage: {
            min: null,
            max: null,
          },
          timeframe: {
            min: null,
            max: null,
          },
          customDosage: {
            min: null,
            max: null,
          },
          formulary: null,
          ivInfusionLocation: null,
          ivInfusionLocationIv: null,
          ivInfusionLocationMedications: null,
          ivInfusionLocationFluids: null,
          customDosingType: null,
        };
    return {
      state: {
        check: merge({}, defaultCheck, check),
        selectedType:
          !check || check.type === 'INFUSION_MEDICATION_SETUP'
            ? 'MEDICATION'
            : 'FLUID',
      },
    };
  },
  computed: {
    isGlobal() {
      return this.emitter.isGlobal;
    },
    selectedDosageForm() {
      const { id: medicationId } = this.state.check;
      if (this.isGlobal) {
        return this.medications.find(med => med._key === medicationId)
      }
      if (!this.emitter.caseConf) {
        return null;
      }
      return this.emitter.caseConf.dosageForms.find(
        caseDosageForm => caseDosageForm.medicationId === medicationId,
      );
    },
    selectedFluid() {
      if (this.isGlobal) {
        return fluids.find(f => f.key === fluidKey);
      }
      const { id: fluidKey } = this.state.check;
      if (!this.emitter.caseConf) {
        return null;
      }
      return this.emitter.caseConf.fluids.find(fluid => fluid.key === fluidKey);
    },
    selectedDosageFormInfusionRoute() {
      if (!this.selectedDosageForm) {
        return null;
      }
      return this.selectedDosageForm.enabledRoutes.find(
        route => route.key === 'IV_IO_INFUSION',
      );
    },
    availableBagVolumes() {
      if (this.state.selectedType === 'MEDICATION') {
        return this.selectedDosageFormInfusionRoute
          ? this.selectedDosageFormInfusionRoute.options.infusionBagVolumes
          : [];
      } else {
        return this.selectedFluid ? this.selectedFluid.infusionBagVolumes : [];
      }
    },
    availableTubings() {
      if (this.isGlobal) {
        return iv.tubings;
      }
      const caseConf = this.emitter.caseConf;
      if (!caseConf.iv) {
        return [];
      }
      const tubings = caseConf.iv.dropSet ? caseConf.iv.dropSet.dripSet : [];
      return iv.tubings.filter(tubing => tubings.includes(tubing.key));
    },
    vtbiUnits() {
      const volumeUnitType = unitTypes.find(unitType => unitType.key === 'VOLUME_ML');
      return [{
        label: volumeUnitType.label.en,
        value: volumeUnitType.key,
      }];
    },
    customDosageUnitLabel() {
      if (!this.state.check.customDosingType) {
        return;
      }
      const dosingType = infusionDosingTypes.find(
        dosingType => dosingType.key === this.state.check.customDosingType
      );
      const unitType = unitTypes.find(unitType => unitType.key === dosingType.numeratorUnits);
      return unitType.label.en;
    },
    availableFormularies() {
      let formularies = [];
      if (this.state.selectedType === 'MEDICATION') {
        formularies =  this.selectedDosageFormInfusionRoute
          ? this.selectedDosageFormInfusionRoute.options.formularies
          : [];
      } else {
        formularies = this.selectedFluid ? this.selectedFluid.formularies || [] : [];
      }
      // Because formularies dont have ids, we use their stringified
      // values as keys for the select, we need to remove the GraphQL
      // typename first though because it is not sent back by apollo
      return formularies.map(formulary => omit(formulary, '__typename'));
    },
    availableDosingTypes() {
      let dosingTypes = [];
      if (this.state.selectedType === 'MEDICATION') {
        dosingTypes =  this.selectedDosageFormInfusionRoute
          ? this.selectedDosageFormInfusionRoute.options.infusionDosingTypes
          : [];
      } else {
        dosingTypes = this.selectedFluid ? this.selectedFluid.infusionDosingTypes || [] : [];
      }
      return dosingTypes.map(dt => {
        const dosingTypeRecord = infusionDosingTypes.find(idt => idt.key === dt);
        return {
          label: dosingTypeRecord ? dosingTypeRecord.label.en : 'Unknown',
          value: dosingTypeRecord.key
        }
      });
    },
    patientWeightAvailable() {
      let dosingType;
      if (this.state.check.function === 'FORMULARY' && this.state.check.formulary) {
        dosingType = this.state.check.formulary.dosing;
      }
      else if (this.state.check.function === 'CUSTOM_DOSE') {
        dosingType = this.state.check.customDosingType;
      }
      return dosingType && dosingType.includes('_KG');
    },
  },
  beforeMount() {
    this.emitter.trigger('process', this.control);
  },
  methods: {
    selectMedication(medicationId) {
      this.state.check.id = medicationId;
      this.state.selectedType = 'MEDICATION';
      this.clearBagVolumes();
      this.clearFormulary();
      this.clearDosingType();
      this.update();
    },
    selectFluid(fluidId) {
      this.state.check.id = fluidId;
      this.state.selectedType = 'FLUID';
      this.clearBagVolumes();
      this.clearFormulary();
      this.clearDosingType();
      this.update();
    },
    updateTarget(target) {
      this.state.check.target = target;
      this.update();
    },
    updateBagVolumes(bagVolumeKeys) {
      this.state.check.bagVolumes = bagVolumeKeys;
      this.update();
    },
    clearBagVolumes() {
      this.state.check.bagVolumes = [];
    },
    updateUnit(unit) {
      this.state.check.unit = unit;
      this.update();
    },
    updateMethod(method) {
      this.state.check.method = method;
      this.update();
    },
    updateFunction(_function) {
      this.state.check.function = _function;
      this.update();
    },
    updateIsPrimaryPump(isPrimary) {
      this.state.check.isPrimaryPump = isPrimary;
      this.update();
    },
    updateDripSet(tubingKeys) {
      this.state.check.dripSets = tubingKeys;
      this.update();
    },
    updateDripSetDosage(dripSetDosage) {
      this.state.check.dripSetDosage = dripSetDosage;
      this.update();
    },
    updateDripSetUnits(dripSetUnits) {
      this.state.check.dripSetUnits = dripSetUnits;
      this.update();
    },
    updateVtbiDosage(vtbiDosage) {
      this.state.check.vtbiDosage = vtbiDosage;
      this.update();
    },
    updateRateDosage(rateDosage) {
      this.state.check.rateDosage = rateDosage;
      this.update();
    },
    updateTimeframe(timeframe) {
      this.state.check.timeframe = timeframe;
      this.update();
    },
    updateCustomDosage(customDosage) {
      this.state.check.customDosage = customDosage;
      this.update();
    },
    updatePatientWeightDosage(patientWeightDosage) {
      this.state.check.patientWeightDosage = patientWeightDosage;
      this.update();
    },
    updateFormulary(formulary) {
      this.state.check.formulary = formulary;
      this.update();
    },
    clearFormulary() {
      this.state.check.formulary = null;
    },
    updateDosingType(dosingType) {
      this.state.check.customDosingType = dosingType;
      this.update();
    },
    clearDosingType() {
      this.state.check.customDosingType = null;
    },
    updateIvInfusionLocation(location) {
      this.state.check.ivInfusionLocation = location;
      this.update();
    },
    updateIvInfusionLocationIv(ivLocation) {
      this.state.check.ivInfusionLocationIv = ivLocation;
      this.update();
    },
    updateIvInfusionLocationMedications(medicationIds) {
      this.state.check.ivInfusionLocationMedications = medicationIds;
      this.update();
    },
    updateIvInfusionLocationFluids(fluidIds) {
      this.state.check.ivInfusionLocationFluids = fluidIds;
      this.update();
    },
    update() {
      const { check, selectedType } = this.state;
      this.putData(this.ikey, {
        ...check,
        type:
          selectedType === 'MEDICATION'
            ? 'INFUSION_MEDICATION_SETUP'
            : 'INFUSION_FLUID_SETUP',
      });
      this.emitter.trigger('process', this.control);
    },
    dosingTypeLabel(dosingTypeKey) {
      const dosingType = infusionDosingTypes.find(d => d.key === dosingTypeKey);
      return dosingType ? dosingType.label.en : 'N/A';
    },
    volumeLabel(volumeKey) {
      const volume = infusionBagVolumes.find(v => v.key === volumeKey);
      return volume ? volume.label.en : 'N/A';
    },
    formularyLabel(formulary) {
      return `${formulary.dose}${this.dosingTypeLabel(formulary.dosing)} (${this.volumeLabel(formulary.volume)})`;
    },
    isOnlyPump() {
      const caseConf = this.emitter.caseConf;
      if (!caseConf || !caseConf.iv) {
        return false;
      }
      return (!caseConf.iv.dropSet || !caseConf.iv.dropSet.enabled) && (caseConf.iv.pump && caseConf.iv.pump.enabled);
    },
  },
};
</script>

<style scoped>
.el-select.error {
  border: solid 3px #f92c2d;
}

.medication-target-selects {
  display: flex;
  align-items: stretch;
}

.function-select {
  height: 33px;
  margin-bottom: 10px;
}

.method-input-group {
  width: auto;
}

.method-select {
  height: 33px;
}

.bag-volume-select-field {
  width: auto;
  margin-right: 0 !important;
}

.formulary-select {
  width: 100%;
  height: 40px;
}

.md-field {
  margin: 0;
}

.md-field >>> .md-select {
  height: 100%;
}

.md-field >>> .md-select input {
  height: 100%;
}

.el-select >>> .el-input {
  height: 100%;
}

.el-select >>> .el-input input {
  height: 100%;
}

.dosage-container {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

.dripset-container {
  display: flex;
  align-items: center;
  background-color: white;
  color: black;
  border-radius: 4px;
  padding: 0 20px;
}

.dripset-container .md-card {
  box-shadow: unset;
}

.dripset-label {
  min-width: 64px;
  margin: 0 10px 0 0;
}

.dripset-select {
  max-width: 250px;
  margin: 0 10px 0 0;
}

.dosage-count-check {
  width: 600px;
}

.push-location-options-container {
  display: flex;
}

.el-card >>> .el-card__body {
  display: flex;
}

.el-card.pump-overview >>> .el-card__body {
  flex-direction: column;
}

.help-tooltip {
  display: flex;
}
</style>
